import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatOptionSelectionChange } from '@angular/material/core';
import { NetworkOrg } from '../model/network-org';
import { StudySetupService } from '../services/study-setup.service';
import { Observable, of, Subscription } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

@Component({
  selector: 'mc-network-org-auto-complete',
  templateUrl: './network-org-auto-complete.component.html',
  styleUrls: ['./network-org-auto-complete.component.scss']
})
export class NetworkOrgAutoCompleteComponent implements OnInit, OnChanges, OnDestroy {

  /** Initial display value for the input */
  @Input() currentOrganizationName = '';
  @Input() cTepId = '';
  /** true = search/display by org name; false = search/display by CTEP ID */
  @Input() isInputOrgName = true;
  @Input() isReadonly = false;
  /** When true the edit input is always visible (no toggle between label/edit modes) */
  @Input() showEditModeOnly = true;
  @Input() controlClass = '';

  @Output() organizationSelected = new EventEmitter<NetworkOrg>();

  formControl = new FormControl<string>('', { nonNullable: true });
  organizationOptions!: Observable<NetworkOrg[]>;
  showEditMode = false;

  private networkList: NetworkOrg[] = [];
  private subscriptions: Subscription[] = [];

  constructor(private studySetupService: StudySetupService) {}

  ngOnInit(): void {
    this.formControl.setValue(this.currentOrganizationName);
    this.showEditMode = this.showEditModeOnly;
    this.loadNetworks();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Keep the displayed value in sync when the parent refills the row
    // (e.g. sibling autocomplete selected an org and updated the binding)
    if (changes['currentOrganizationName'] && !changes['currentOrganizationName'].firstChange) {
      this.formControl.setValue(changes['currentOrganizationName'].currentValue ?? '', { emitEvent: false });
    }
  }

  private loadNetworks(): void {
    this.subscriptions.push(
      this.studySetupService.getAllNetworkOrganizations().subscribe(list => {
        this.networkList = list;

        this.organizationOptions = this.formControl.valueChanges.pipe(
          startWith(''),
          map((value: string | NetworkOrg) => typeof value === 'string'
            ? value
            : (this.isInputOrgName ? (value as NetworkOrg).name ?? '' : (value as NetworkOrg).ctepId ?? '')
          ),
          map((query: string) => query && query.trim() !== '' ? this.filter(query) : [])
        );
      })
    );
  }

  private filter(query: string): NetworkOrg[] {
    const q = query.toLowerCase();
    return this.networkList.filter(org =>
      this.isInputOrgName
        ? org.name?.toLowerCase().indexOf(q) === 0
        : org.ctepId?.toLowerCase().indexOf(q) === 0
    );
  }

  displayFn(org: NetworkOrg | string): string {
    if (typeof org === 'string') { return org; }
    if (!org) { return ''; }
    return this.isInputOrgName ? (org.name ?? '') : (org.ctepId ?? '');
  }

  handleSelectSuggestion(event: MatOptionSelectionChange): void {
    if (event?.isUserInput) {
      const org: NetworkOrg = event.source.value;
      this.organizationSelected.emit(org);
    }
  }

  getPlaceholder(): string {
    return this.isInputOrgName ? 'Organization Name' : 'CTEP ID';
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
    this.subscriptions.length = 0;
  }
}
